import React, { useEffect, useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";

/* API base */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* Theme */
const theme = {
  brand: "#3dccc7",
  accent: "#ff914d",
  white: "#f1f1f1",
  subtext: "#d6d9de",
  card: "#0f1b2d",
  border: "#1e293b",
  bg: "#0b1320",
};

const Wrap = styled.div`display: grid; gap: 16px;`;
const TitleRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 12px;
`;
const Title = styled.h2`
  margin: 0; font-size: clamp(18px, 2.4vw, 22px); font-weight: 800; color: ${theme.white};
`;
const FilterBox = styled.select`
  background: ${theme.card}; border: 1px solid ${theme.border}; color: ${theme.white};
  font-size: 14px; border-radius: 10px; padding: 8px 12px; outline: none; cursor: pointer;
  option { background: #0b1320; color: ${theme.white}; }
`;
const DateInput = styled.input`
  background: ${theme.card}; border: 1px solid ${theme.border}; color: ${theme.white};
  font-size: 14px; border-radius: 10px; padding: 8px 12px; outline: none; cursor: pointer;
`;
const Button = styled.button`
  background: ${theme.accent}; color: ${theme.white}; font-weight: 700;
  border: none; border-radius: 10px; padding: 8px 16px; cursor: pointer;
  transition: background 0.2s ease;
  &:hover { background: #ff7a1f; }
`;
const TableShell = styled.div`
  border: 1px solid ${theme.border};
  background: linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01));
  border-radius: 16px; overflow: hidden;
`;
const Scroll = styled.div`max-width: 100%; overflow: auto;`;
const Table = styled.table`
  width: 100%; border-collapse: separate; border-spacing: 0; min-width: 900px;
`;
const Thead = styled.thead`
  background: linear-gradient(180deg, #0f1b2d, #0b1626);
  position: sticky; top: 0; z-index: 1;
`;
const Th = styled.th`
  text-align: left; padding: 14px 16px; font-size: 12px; color: ${theme.subtext};
  letter-spacing: 0.5px; text-transform: uppercase; border-bottom: 1px solid ${theme.border};
  white-space: nowrap;
  &:first-child { padding-left: 18px; }
  &:last-child { padding-right: 18px; }
`;
const Tbody = styled.tbody``;
const Tr = styled.tr`
  transition: background 0.12s ease;
  &:nth-child(even) { background: rgba(255,255,255,0.02); }
  &:hover { background: rgba(255,255,255,0.05); }
`;
const Td = styled.td`
  padding: 14px 16px; font-size: 14px; color: ${theme.white};
  border-bottom: 1px dashed ${theme.border}; vertical-align: middle;
  &:first-child { padding-left: 18px; }
  &:last-child { padding-right: 18px; }
`;
const Email = styled.a`
  color: ${theme.white}; text-decoration: none; border-bottom: 1px dashed rgba(255,255,255,0.3);
  &:hover { color: ${theme.accent}; border-bottom-color: ${theme.accent}; }
`;
const Chip = styled.span`
  display: inline-block; padding: 6px 10px; font-size: 12px; font-weight: 800;
  border-radius: 999px; color: #111;
  background: ${({ $yes }) => ($yes ? "#6ee7b7" : "#fecaca")};
  border: 1px solid ${({ $yes }) => ($yes ? "rgba(16,185,129,0.35)" : "rgba(248,113,113,0.35)")};
`;
const Meta = styled.div`color: ${theme.subtext}; font-size: 13px;`;
const ErrorBanner = styled.div`
  background: #fdecea; color: #611a15; border: 1px solid #f5c6cb;
  border-radius: 12px; padding: 8px 12px; font-size: 0.95rem;
`;

export default function EbookEmails() {
  const [rows, setRows] = useState([]);        // raw from server
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // filters
  const [filter, setFilter] = useState("all"); // webinar: all|yes|no
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [endDate, setEndDate] = useState("");     // YYYY-MM-DD

  // Fetch from server whenever filters change
  useEffect(() => {
    const fetchData = async () => {
      if (!API_BASE) {
        setErr("API base URL not configured.");
        return;
      }
      setErr("");
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filter && filter !== "all") params.set("webinar", filter);
        if (startDate) params.set("startDate", startDate);
        if (endDate) params.set("endDate", endDate);
        params.set("sort", "-createdAt");
        params.set("limit", "1000"); // pull more; paginate later if needed

        // NOTE: using your route path as provided
        const res = await fetch(`${API_BASE}/api/email-leads/get-leads?${params.toString()}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) {
          throw new Error(payload?.message || "Failed to load leads");
        }

        const mapped = (payload.data || []).map((d, idx) => ({
          // keep db id ONLY for React key; do NOT display it
          _id: d.id,
          first: d.firstName,
          last: d.lastName,
          email: d.email,
          interested: !!d.webinarSignup,
          date: d.createdAt ? d.createdAt.slice(0, 10) : "", // YYYY-MM-DD
          _i: idx, // local index fallback for key
        }));

        setRows(mapped);
      } catch (e) {
        setErr(e.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filter, startDate, endDate]);

  const total = rows.length;

  const downloadExcel = () => {
    // Use serial numbers in export, not database ids
    const wsData = rows.map((r, i) => ({
      "S. No.": i + 1,
      "First Name": r.first,
      "Last Name": r.last,
      Email: r.email,
      "Webinar Interest": r.interested ? "Yes" : "No",
      "Date Filled": r.date,
    }));

    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ebook Submissions");

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    XLSX.writeFile(wb, `Ebook_Emails_${yyyy}-${mm}-${dd}.xlsx`);
  };

  return (
    <Wrap>
      <TitleRow>
        <Title>E-book Form Submissions</Title>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <Meta>Total records: {loading ? "…" : total} |</Meta>

          <Meta>Filter Records - Webinar Interest </Meta>
          <FilterBox value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="yes">Interested: Yes</option>
            <option value="no">Interested: No</option>
          </FilterBox>

          <Meta>Date Range:</Meta>
          <DateInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <DateInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />

          <Button onClick={downloadExcel} disabled={loading || !rows.length}>
            {loading ? "Preparing…" : "Download Emails"}
          </Button>
        </div>
      </TitleRow>

      {err && <ErrorBanner>{err}</ErrorBanner>}

      <TableShell>
        <Scroll>
          <Table>
            <Thead>
              <Tr>
                <Th>S. No.</Th> {/* was Id */}
                <Th>First name</Th>
                <Th>Last name</Th>
                <Th>Email id</Th>
                <Th>Interested for upcoming webinars</Th>
                <Th>Date Filled</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr><Td colSpan={6}>Loading…</Td></Tr>
              ) : rows.length ? (
                rows.map((r, idx) => (
                  <Tr key={r._id || r._i}>
                    <Td>{idx + 1}</Td> {/* Serial number only */}
                    <Td>{r.first}</Td>
                    <Td>{r.last}</Td>
                    <Td><Email href={`mailto:${r.email}`}>{r.email}</Email></Td>
                    <Td><Chip $yes={r.interested}>{r.interested ? "Yes" : "No"}</Chip></Td>
                    <Td>{r.date}</Td>
                  </Tr>
                ))
              ) : (
                <Tr><Td colSpan={6}>No records found.</Td></Tr>
              )}
            </Tbody>
          </Table>
        </Scroll>
      </TableShell>
    </Wrap>
  );
}
